import { useState, useMemo } from "react";
import { Search, X, ChevronDown, ChevronUp, FlaskConical } from "lucide-react";
import { cn } from "@/lib/cn";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import type { Biometric } from "@/lib/api_client";

interface BiometricPickerProps {
  selected: string[];
  onChange: (ids: string[]) => void;
  biometrics: Biometric[];
  label?: string;
}

export function BiometricPicker({ selected, onChange, biometrics, label = "Anbefalte biomarkorer" }: BiometricPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    const list = biometrics.filter((b) =>
      !q || (b.name ?? "").toLowerCase().includes(q) || (b.shortName ?? "").toLowerCase().includes(q)
    );
    // Sort selected to top, then alphabetically
    return list.sort((a, b) => {
      const aS = selectedSet.has(a.id) ? 0 : 1;
      const bS = selectedSet.has(b.id) ? 0 : 1;
      if (aS !== bS) return aS - bS;
      return (a.name ?? "").localeCompare(b.name ?? "");
    });
  }, [biometrics, search, selectedSet]);

  const toggle = (id: string) => {
    onChange(
      selectedSet.has(id) ? selected.filter((s) => s !== id) : [...selected, id]
    );
  };

  const selectedBiometrics = useMemo(
    () => biometrics.filter((b) => selectedSet.has(b.id)).sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")),
    [biometrics, selectedSet]
  );

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          "flex items-center gap-2 w-full text-left text-xs py-1.5 px-2 rounded-md transition-colors cursor-pointer",
          "hover:bg-admin-card-hover",
          open && "bg-admin-card-hover"
        )}
      >
        <FlaskConical className="h-3 w-3 text-admin-text-subtle shrink-0" />
        <span className="text-admin-text-subtle font-medium">{label}</span>
        <span className="text-admin-text-subtle ml-auto">{selected.length > 0 ? `${selected.length} valgt` : "Ingen"}</span>
        {open ? <ChevronUp className="h-3 w-3 text-admin-text-subtle" /> : <ChevronDown className="h-3 w-3 text-admin-text-subtle" />}
      </button>

      {open && (
        <div className="border border-admin-card-border rounded-md bg-admin-card p-2 space-y-2">
          {selectedBiometrics.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {selectedBiometrics.map((b) => (
                <Badge
                  key={b.id}
                  variant="default"
                  className="text-[10px] pr-1 gap-1 cursor-pointer hover:opacity-80"
                  onClick={() => toggle(b.id)}
                >
                  {b.shortName || b.name}
                  <X className="h-2.5 w-2.5" />
                </Badge>
              ))}
            </div>
          )}

          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-admin-text-subtle" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Sok biomarkorer..."
              className="h-7 text-xs pl-7"
            />
          </div>

          <div className="max-h-48 overflow-y-auto space-y-0.5">
            {filtered.map((b) => {
              const isSelected = selectedSet.has(b.id);
              return (
                <label
                  key={b.id}
                  className={cn(
                    "flex items-center gap-2 px-2 py-1 rounded text-xs cursor-pointer transition-colors",
                    isSelected ? "bg-admin-primary/10" : "hover:bg-admin-card-hover"
                  )}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggle(b.id)}
                    className="accent-admin-primary shrink-0"
                  />
                  <span className={cn("truncate", isSelected && "font-medium")}>{b.name}</span>
                  {b.shortName && b.shortName !== b.name && (
                    <span className="text-admin-text-subtle ml-auto shrink-0">{b.shortName}</span>
                  )}
                </label>
              );
            })}
            {filtered.length === 0 && (
              <p className="text-xs text-admin-text-subtle text-center py-2">Ingen treff</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
